/* Owner Dashboard widget — Kitchen Readiness by Branch (Phase 2A).

Reuses /api/prepare/branch-readiness (which itself reuses /api/prepare/queue for each
branch — no duplicate calc). Renders per-branch score, band, and completion counters
with a "View" link straight into Meja Prepare for that branch.
*/
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChefHat, ArrowUpRight, AlertTriangle, RefreshCcw } from "lucide-react";
import api from "../lib/api";
import { Card, Badge, Button } from "../lib/ui";
import { useT } from "../lib/i18n";

const BAND_META = {
  ready: { tone: "success", label: "Ready", bar: "bg-emerald-500", emoji: "🟢" },
  partial: { tone: "warn", label: "Partial", bar: "bg-amber-500", emoji: "🟡" },
  not_ready: { tone: "danger", label: "Not Ready", bar: "bg-red-500", emoji: "🔴" },
};

export default function KitchenReadinessWidget() {
  const { t } = useT();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    setErr(null);
    try {
      const { data } = await api.get("/prepare/branch-readiness");
      setRows(data.branches || []);
    } catch (e) {
      setRows([]);
      setErr(e.response?.data?.detail || t("Gagal memuat data"));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (rows === null) {
    return (
      <Card className="p-5" data-testid="kitchen-readiness-widget">
        <div className="animate-pulse text-sm text-ink-mute">{t("Loading Kitchen Readiness...")}</div>
      </Card>
    );
  }
  if (rows.length === 0 && !err) return null; // no branches -> hide silently

  return (
    <Card className="p-5" data-testid="kitchen-readiness-widget">
      <div className="mb-4 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50 text-orange-600"><ChefHat size={20} /></span>
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-base font-extrabold text-ink">{t("Kitchen Readiness per Cabang")}</h3>
          <p className="text-[11px] text-ink-mute">{t("Otomatis dari Master Persiapan · Target Standby vs stok Bahan Siap Masak aktual")}</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="kitchen-readiness-refresh"><RefreshCcw size={13} /></Button>
      </div>
      {err && (
        <div className="mb-3 flex items-start gap-2 rounded-xl bg-danger-light/40 px-3 py-2 text-xs text-danger-text" data-testid="kitchen-readiness-error">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" /> <span>{err}</span>
        </div>
      )}
      <div className="space-y-2" data-testid="kitchen-readiness-list">
        {rows.map((r) => {
          const meta = BAND_META[r.readiness_band] || BAND_META.not_ready;
          const score = r.readiness_score ?? 0;
          return (
            <div key={r.branch_id} className="rounded-xl border border-line p-3" data-testid={`kitchen-readiness-branch-${r.branch_name}`}>
              <div className="mb-2 flex items-center gap-2">
                <p className="min-w-0 flex-1 truncate font-heading text-sm font-extrabold text-ink">{r.branch_name}</p>
                <Badge tone={meta.tone} data-testid={`kitchen-readiness-band-${r.branch_name}`}>{meta.emoji} {r.readiness_score ?? "—"}%</Badge>
                <button
                  onClick={() => navigate("/meja-prepare", { state: { warehouse_id: r.branch_id } })}
                  data-testid={`kitchen-readiness-open-${r.branch_name}`}
                  className="rounded-lg border border-line p-1 text-ink-mute hover:border-primary hover:text-primary">
                  <ArrowUpRight size={14} />
                </button>
              </div>
              <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-stone-100">
                <div className={`h-full rounded-full ${meta.bar}`} style={{ width: `${score}%` }} />
              </div>
              <div className="flex flex-wrap gap-1.5 text-[10px]">
                <Badge tone="success">🟢 {r.complete || 0} {t("Complete")}</Badge>
                <Badge tone="warn">🟡 {r.partial || 0} {t("Partial")}</Badge>
                <Badge tone="danger">🔴 {r.not_started || 0} {t("Not Started")}</Badge>
                {r.critical > 0 && <Badge tone="danger">⚠️ {r.critical} {t("Kritis")}</Badge>}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
